/**
 * The gazetteer-searchable form of a typed birth place: the ONE function the
 * autocomplete (dropdown, remote search, blur exact-commit) and the submit-time
 * resolver both call, so they can never disagree about what a name means.
 *
 * Two closed, verified tables apply, in order:
 *   1. Devanagari big-city names -> English (place-devanagari.ts).
 *   2. Old English city names -> the name the gazetteer uses (below).
 *
 * Why (2) exists (review of C07, 2026-10-07): an exact name match is shown
 * alone and committed on blur, so a seeker typing "Calcutta" got Calcutta,
 * South Africa (wrong country and timezone), and "Baroda" got the 18k-person
 * Baroda in Madhya Pradesh instead of Vadodara. Kolkata and Vadodara only carry
 * those names as GeoNames alternates. For the old names without a namesake the
 * search already found the city, but the seeker still had to tap a "did you
 * mean" chip; now the name locks like any exact name.
 *
 * Only a BARE name is rewritten ("Baroda", "baroda, India"). A name the seeker
 * qualified ("Baroda, Madhya Pradesh") is searched as typed, so the namesake
 * stays reachable; for the two namesakes that are real local towns (Baroda MP,
 * Bijapur CG) the autocomplete also lists them under the renamed city.
 *
 * Entry rule: the old name is a GeoNames alternate name of the canonical city
 * (checked against the D1 copy), and the canonical is an exact name in
 * public/data/place-cities.json (pinned by the test). Names the local index
 * itself uses (Sholapur, Tumkur, Naini Tal, Saugor) are never rewritten, and
 * genuine same-name twins (Aurangabad MH/BR, Sagar KA/MP) are left alone.
 */
import { devanagariToGazetteerQuery } from "./place-devanagari";

export const LEGACY_CITY_NAMES: ReadonlyArray<readonly [legacy: string, canonical: string]> = [
  ["ahmadabad", "Ahmedabad"],
  ["allahabad", "Prayagraj"],
  ["alleppey", "Alappuzha"],
  ["banaras", "Varanasi"],
  ["bangalore", "Bengaluru"],
  ["baroda", "Vadodara"],
  ["belgaum", "Belagavi"],
  ["bellary", "Ballari"],
  ["benares", "Varanasi"],
  ["bhubaneshwar", "Bhubaneswar"],
  ["bijapur", "Vijayapura"],
  ["bombay", "Mumbai"],
  ["calcutta", "Kolkata"],
  ["calicut", "Kozhikode"],
  ["cannanore", "Kannur"],
  ["cawnpore", "Kanpur"],
  ["cochin", "Kochi"],
  ["dehra dun", "Dehradun"],
  ["gauhati", "Guwahati"],
  ["gulbarga", "Kalaburagi"],
  ["gurgaon", "Gurugram"],
  ["hoshangabad", "Narmadapuram"],
  ["hubli", "Hubballi"],
  ["jubbulpore", "Jabalpur"],
  ["kashi", "Varanasi"],
  ["madras", "Chennai"],
  ["mangalore", "Mangaluru"],
  ["midnapore", "Medinipur"],
  ["mysore", "Mysuru"],
  ["nasik", "Nashik"],
  ["osmanabad", "Dharashiv"],
  ["palghat", "Palakkad"],
  ["pondicherry", "Puducherry"],
  ["poona", "Pune"],
  ["quilon", "Kollam"],
  ["shimoga", "Shivamogga"],
  ["simla", "Shimla"],
  ["tanjore", "Thanjavur"],
  ["trichur", "Thrissur"],
  ["trichy", "Tiruchirappalli"],
  ["trivandrum", "Thiruvananthapuram"],
  ["tuticorin", "Thoothukudi"],
  ["vizag", "Visakhapatnam"],
];

const LEGACY_CITY_MAP: ReadonlyMap<string, string> = new Map(LEGACY_CITY_NAMES);

function bareNameKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/,\s*india$/, "")
    .replace(/\s+/g, " ")
    .trim();
}

export interface PlaceQuery {
  /** What to match and search with. */
  query: string;
  /** The typed old name (lower case) when a legacy rewrite applied, else null. */
  namesake: string | null;
}

export function resolvePlaceQuery(typed: string): PlaceQuery {
  const query = devanagariToGazetteerQuery(typed);
  const key = bareNameKey(query);
  const canonical = LEGACY_CITY_MAP.get(key);
  return canonical ? { query: canonical, namesake: key } : { query, namesake: null };
}

export function toGazetteerQuery(typed: string): string {
  return resolvePlaceQuery(typed).query;
}
